import { Suspense, lazy } from 'react';
import { Navigate, Outlet, RouteObject } from 'react-router';

import { FeatureToggle, relativeRoutes, routes } from '@fly/shared';
import { FeatureGuard } from '@fly/shared-ui';

import {
  BaseBreadcrumb,
  BaseLayout,
  RegistryLayout,
  RepositoryLayout,
  TablePageSkeleton,
} from './components';
import { ArtifactVersionsBreadcrumb } from './containers/ArtifactVersions/ArtifactVersionsBreadcrumb';
import { ArtifactsBreadcrumb } from './containers/Artifacts/ArtifactsBreadcrumb';
import { ReleaseArtifactsBreadcrumb } from './containers/ReleaseArtifacts/components/ReleaseArtifactsBreadcrumb';
import { StandaloneArtifactVersionsBreadcrumb } from './containers/StandaloneArtifactVersions/StandaloneArtifactVersionsBreadcrumb';

const Repositories = lazy(
  () => import('./containers/Repositories/Repositories')
);
const StandaloneArtifactVersions = lazy(
  () => import('./containers/StandaloneArtifactVersions')
);
const Artifacts = lazy(() => import('./containers/Artifacts'));
const Releases = lazy(() => import('./containers/Releases'));
const ArtifactVersions = lazy(() => import('./containers/ArtifactVersions'));
const AllArtifacts = lazy(() => import('./containers/AllArtifacts'));
const Workflows = lazy(() => import('./containers/Workflows'));
const ReleaseArtifacts = lazy(() => import('./containers/ReleaseArtifacts'));
const RepositoryDecisionRecords = lazy(
  () => import('./containers/RepositoryDecisionRecords')
);

export const registryRoutes: RouteObject[] = [
  {
    path: `${routes.DASHBOARD.REGISTRY.ROOT}/*`,
    element: <BaseLayout />,
    handle: {
      Breadcrumb: BaseBreadcrumb,
    },
    children: [
      {
        index: true,
        element: (
          <Navigate to={routes.DASHBOARD.REGISTRY.GIT_REPOSITORIES} replace />
        ),
      },
      {
        path: `${routes.DASHBOARD.REGISTRY.ROOT}/*`,
        element: <RegistryLayout />,
        children: [
          {
            path: `${relativeRoutes.GIT_REPOSITORIES}`,
            element: (
              <Suspense fallback={<TablePageSkeleton />}>
                <Repositories />
              </Suspense>
            ),
          },
          {
            path: `${relativeRoutes.ARTIFACTS}`,
            element: (
              <Suspense fallback={<TablePageSkeleton />}>
                <AllArtifacts />
              </Suspense>
            ),
          },
          {
            path: '*',
            element: (
              <Navigate
                to={routes.DASHBOARD.REGISTRY.GIT_REPOSITORIES}
                replace
              />
            ),
          },
        ],
      },
      {
        path: `${relativeRoutes.GIT_REPOSITORIES}/:owner/:name/*`,
        element: <Outlet />,
        handle: {
          Breadcrumb: ArtifactsBreadcrumb,
        },
        children: [
          {
            index: true,
            element: <Navigate to={relativeRoutes.RELEASES} replace />,
          },
          {
            element: <RepositoryLayout />,
            children: [
              {
                path: `${relativeRoutes.RELEASES}`,
                element: (
                  <Suspense fallback={<TablePageSkeleton />}>
                    <Releases />
                  </Suspense>
                ),
              },
              {
                path: `${relativeRoutes.ARTIFACTS}`,
                element: (
                  <Suspense fallback={<TablePageSkeleton />}>
                    <Artifacts />
                  </Suspense>
                ),
              },
              {
                path: `${relativeRoutes.WORKFLOWS}`,
                element: (
                  <Suspense fallback={<TablePageSkeleton />}>
                    <Workflows />
                  </Suspense>
                ),
              },
              {
                path: `${relativeRoutes.DECISION_RECORDS}`,
                element: (
                  <FeatureGuard feature={FeatureToggle.DECISION_RECORDS}>
                    <Suspense fallback={<TablePageSkeleton />}>
                      <RepositoryDecisionRecords />
                    </Suspense>
                  </FeatureGuard>
                ),
              },
              {
                path: '*',
                element: (
                  <Navigate to={`../${relativeRoutes.RELEASES}`} replace />
                ),
              },
            ],
          },
          {
            path: `${relativeRoutes.ARTIFACTS}/:packageType/:artifactName/${relativeRoutes.ARTIFACT_VERSIONS}`,
            element: (
              <Suspense fallback={<TablePageSkeleton />}>
                <ArtifactVersions />
              </Suspense>
            ),
            handle: {
              Breadcrumb: ArtifactVersionsBreadcrumb,
            },
          },
          {
            path: `${relativeRoutes.RELEASES}/:workflowName/:runNumber`,
            element: (
              <Navigate relative='path' to={relativeRoutes.ARTIFACTS} replace />
            ),
          },
          {
            path: `${relativeRoutes.RELEASES}/:workflowName/:runNumber/`,
            element: (
              <Navigate relative='path' to={relativeRoutes.ARTIFACTS} replace />
            ),
          },
          {
            path: `${relativeRoutes.RELEASES}/:workflowName/:runNumber/${relativeRoutes.ARTIFACTS}`,
            element: (
              <Suspense fallback={<TablePageSkeleton />}>
                <ReleaseArtifacts />
              </Suspense>
            ),
            handle: {
              Breadcrumb: ReleaseArtifactsBreadcrumb,
            },
          },
        ],
      },
      {
        path: `${relativeRoutes.ARTIFACTS}/:packageType/:artifactName/${relativeRoutes.ARTIFACT_VERSIONS}`,
        element: (
          <Suspense fallback={<TablePageSkeleton />}>
            <StandaloneArtifactVersions />
          </Suspense>
        ),
        handle: {
          Breadcrumb: StandaloneArtifactVersionsBreadcrumb,
        },
      },
    ],
  },
];
